import _map from 'lodash/map';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import _size from 'lodash/size';
import _keyBy from 'lodash/keyBy';
import { getStateFromZipcode } from './zipcode-utils';

export const FULFILLMENT_TYPES = {
  bopis: 'bopis',
  sth: 'sth',
  boss: 'boss',
  bodfs: 'express delivery'
};

export const FULFILLMENT_METHODS = {
  bopis: 'BOPIS',
  sth: 'ShipToHome',
  boss: 'ShipToStore',
  bodfs: 'DeliverFromStore'
};

const excludedShippingCondition = (flag, serviceType) => {
  if (flag) {
    return (
      serviceType === FULFILLMENT_TYPES.bopis
      || serviceType === FULFILLMENT_TYPES.bodfs
      || serviceType === FULFILLMENT_TYPES.boss
    );
  }

  return (
    serviceType === FULFILLMENT_TYPES.sth
    || serviceType === FULFILLMENT_TYPES.bopis
    || serviceType === FULFILLMENT_TYPES.boss
    || serviceType === FULFILLMENT_TYPES.bodfs
  );
};

const excludedBossShippingCondition = (flag, serviceType) => {
  if (flag) {
    return (
      serviceType === FULFILLMENT_TYPES.bopis
      || serviceType === FULFILLMENT_TYPES.bodfs
    );
  }

  return (
    serviceType === FULFILLMENT_TYPES.sth
    || serviceType === FULFILLMENT_TYPES.bopis
    || serviceType === FULFILLMENT_TYPES.boss
    || serviceType === FULFILLMENT_TYPES.bodfs
  );
};

const checkServiceLocationInventory = (
  service, inEligibleShipping = false, inEligibleStoreShipping = false
) => {
  if (inEligibleShipping || inEligibleStoreShipping) {
    return _find(
      service?.locations,
      (location) => (
        (
          excludedShippingCondition(inEligibleShipping, service.type)
          || excludedBossShippingCondition(inEligibleStoreShipping, service.type)
        )
        && location?.inventory?.quantity > 0
      ));
  }
  return _find(
    service?.locations,
    (location) => (location?.inventory?.quantity > 0));
};

const mapFulfillmentOptions = (
  fulfillmentOptions, inEligibleShipping = false, inEligibleStoreShipping = false
) => {
  return _map(fulfillmentOptions, (fulfillmentOption) => ({
    type: fulfillmentOption?.type,
    inExcludedShipState: inEligibleShipping,
    inExcludedStoreShipState: inEligibleStoreShipping,
    services: _map(fulfillmentOption?.services, (service) => {
      const locations = checkServiceLocationInventory(
        service, inEligibleShipping, inEligibleStoreShipping
      );
      return ({
        type: service?.type,
        fulfillable: (locations && _size(locations) > 0) || false
      });
    })
  }));
};

export const getFulfillmentMethodAndLocation = (
  fulfillmentInfo, storeId, storeZip, zipCode
) => {
  const delivery = _find(fulfillmentInfo?.delivery?.services, (service) => service.fulfillable === true);
  const pickup = _find(fulfillmentInfo?.pickup?.services, (service) => service.fulfillable === true);
  const inExcludedShipState = fulfillmentInfo?.delivery?.inExcludedShipState;
  const inExcludedStoreShipState = fulfillmentInfo?.pickup?.inExcludedStoreShipState;

  if (inExcludedShipState) {
    if (pickup?.type === FULFILLMENT_TYPES.bopis) {
      return [FULFILLMENT_METHODS.bopis, storeId];
    }
    if (pickup?.type === FULFILLMENT_TYPES.boss) {
      return [FULFILLMENT_METHODS.boss, storeId];
    }
    if (delivery?.type === FULFILLMENT_TYPES.bodfs) {
      return [FULFILLMENT_METHODS.bodfs, storeZip];
    }

    return [FULFILLMENT_METHODS.bopis, storeId];
  }

  if (inExcludedStoreShipState) {
    if (pickup?.type === FULFILLMENT_TYPES.bopis) {
      return [FULFILLMENT_METHODS.bopis, storeId];
    }
    if (delivery?.type === FULFILLMENT_TYPES.bodfs) {
      return [FULFILLMENT_METHODS.bodfs, storeZip];
    }
  }

  if (delivery?.type === FULFILLMENT_TYPES.sth) {
    return [FULFILLMENT_METHODS.sth, zipCode]; // use localized zipcode
  }
  if (pickup?.type === FULFILLMENT_TYPES.bopis) {
    return [FULFILLMENT_METHODS.bopis, storeId]; // use store number
  }
  if (pickup?.type === FULFILLMENT_TYPES.boss) {
    return [FULFILLMENT_METHODS.boss, storeId]; // use store number
  }
  if (delivery?.type === FULFILLMENT_TYPES.bodfs) {
    return [FULFILLMENT_METHODS.bodfs, storeZip]; // use store zipcode
  }

  return [FULFILLMENT_METHODS.sth, zipCode]; // use localized zipcode
};

export const getLocalizerZipCode = () => {
  if (typeof window !== 'undefined') {
    const zipDetails = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    return zipDetails?.zipcode || '30339';
  }
  return '30339';
};

export const checkFulfillment = (product, zipcode) => {
  if (_isEmpty(product)) return {};
  const productFulfillmentOptions = product?.fulfillment?.fulfillmentOptions;

  if (!productFulfillmentOptions) {
    return null;
  }

  const excludedShipStatesList = product?.fulfillment?.excludedShipStates?.split(',');
  const bossExcludedShipStatesList = product?.fulfillment?.bossExcludedShipStates?.split(',');
  const zipcodeState = getStateFromZipcode(zipcode); // returns state abbreviation
  const inEligibleShipping = excludedShipStatesList.indexOf(zipcodeState) !== -1;
  const inEligibleStoreShipping = bossExcludedShipStatesList.indexOf(zipcodeState) !== -1;

  if (inEligibleShipping) {
    return _keyBy(
      mapFulfillmentOptions(
        productFulfillmentOptions, inEligibleShipping, inEligibleStoreShipping
      ),
      'type'
    );
  }

  return _keyBy(
    mapFulfillmentOptions(
      productFulfillmentOptions, inEligibleShipping, inEligibleStoreShipping
    ),
    'type'
  );
};
